import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HiX, HiCheck } from 'react-icons/hi';
import { IOrderProduct } from '../../../../types/OrderProduct';
import { PublicProduct } from '../../../../api/publicOrder';

interface CartDropdownProps {
    selectedProducts: IOrderProduct[];
    products: PublicProduct[];
    onClose: () => void;
    onProceedToDetails: () => void;
    canProceedToDetails: boolean;
    currentStep: number;
}

const CartDropdown: React.FC<CartDropdownProps> = ({
    selectedProducts,
    products,
    onClose,
    onProceedToDetails,
    canProceedToDetails,
    currentStep
}) => {
    const { t } = useTranslation(['order_form']);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    const getProductDetails = (productId: string | undefined) => {
        if (!productId) return null;
        return products.find(p => p.id === productId);
    };

    const formatPrice = (price: number | null | undefined) => {
        if (price === null || price === undefined) return t('public_form.common.price_on_request');
        return `${price.toFixed(2)} ${t('public_form.common.currency')}`;
    };

    const getProductPrice = (product: PublicProduct): number | null => {
        if (!product.pricing) return null;
        
        const { pricePerCargoUnit, pricePerDistanceUnit, pricePerVolumeUnit, pricePerWeightUnit, pricePerTimeUnit } = product.pricing;
        return pricePerCargoUnit || pricePerDistanceUnit || pricePerVolumeUnit || pricePerWeightUnit || pricePerTimeUnit || null;
    };

    return (
        <div className="absolute right-0 top-14 z-[60]">
            <div 
                ref={dropdownRef}
                className="bg-white rounded-lg shadow-xl border border-gray-200 w-96 max-h-96 overflow-hidden"
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {t('public_form.cart.title')} ({selectedProducts.length})
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-muted hover:text-gray-600 transition-colors"
                    >
                        <HiX className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="max-h-64 overflow-y-auto">
                    {selectedProducts.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">
                            {t('public_form.cart.empty')}
                        </div>
                    ) : (
                        <div className="p-4 space-y-3">
                            {selectedProducts.map((item, index) => {
                                const product = getProductDetails(item.productId);
                                if (!product) return null;

                                return (
                                    <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                                        {/* Product Image */}
                                        <div className="w-12 h-12 flex-shrink-0">
                                            {product.imgUrls && product.imgUrls.length > 0 ? (
                                                <img
                                                    src={product.imgUrls[0]}
                                                    alt={product.descriptionShort}
                                                    className="w-full h-full object-cover rounded-lg"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.style.display = 'none';
                                                        target.nextElementSibling?.classList.remove('hidden');
                                                    }}
                                                />
                                            ) : null}
                                            <div className={`w-full h-full bg-gray-200 rounded-lg flex items-center justify-center text-muted text-xs ${
                                                product.imgUrls && product.imgUrls.length > 0 ? 'hidden' : ''
                                            }`}>
                                                {t('public_form.common.no_image')}
                                            </div>
                                        </div>

                                        {/* Product Details */}
                                        <div className="flex-1 min-w-0">
                                            <h4 className="text-sm font-medium text-gray-900 truncate">
                                                {product.descriptionShort}
                                            </h4>
                                            <div className="flex items-center justify-between mt-1">
                                                <span className="text-sm text-gray-500">
                                                    {t('public_form.common.quantity')}: {item.quantity}
                                                </span>
                                                <span className="text-sm font-medium text-gray-900">
                                                    {formatPrice(getProductPrice(product))}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Selected indicator */}
                                        <div className="flex-shrink-0">
                                            <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                <HiCheck className="h-3 w-3 mr-1" />
                                                {t('public_form.common.selected')}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                {selectedProducts.length > 0 && (
                    <div className="p-4 border-t border-gray-200">
                        <button
                            onClick={onProceedToDetails}
                            disabled={!canProceedToDetails}
                            className={`w-full py-2 px-4 rounded-lg font-medium transition-all duration-200 ${
                                canProceedToDetails
                                    ? 'public-form-primary-bg text-white hover:bg-opacity-90 shadow-sm'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                        >
                            {currentStep === 1 ? t('public_form.cart.continue_to_details') : t('public_form.cart.continue')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CartDropdown; 